import { clientConfig } from '@/config/clientConfig';
import type { JobListItem } from '@/domains/jobs/api/jobListApi';
import { canRequestMoreInfo } from '@/domains/jobs/constants/canRequestMoreInfo';
import { cn } from '@/shared/lib/cn';
import { Badge } from '@/shared/ui/badge';
import { ArrowUpRight, CheckCheck, MessageSquare, Reply } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

type MirBadgeState = JobListItem['mir_badge_state'];

interface MirBadgeSpec {
  label: string;
  icon: LucideIcon;
  className: string;
}

/**
 * One badge per MIR state for a job row, replacing the three separately-gated
 * badges the list used to render. Those could disagree with the detail page —
 * and 'inbound' fell through all of them, so the list showed nothing where the
 * detail page showed "Reply Required" (WRK-5381).
 *
 * Record<MirBadgeState, …> makes this exhaustive: adding a state to the contract
 * enum fails the build here until it is given a spec. `none` maps to null —
 * absence of a badge already means "no conversation", and the detail page's
 * MirStatusBadge renders nothing for that state too.
 *
 * Labels, colours and icons are copied from the detail page's MirStatusBadge (and
 * the AwaitingReplyBadge / ResponseReceivedBadge it composes) so the two surfaces
 * read identically.
 */
const MIR_BADGE_SPECS: Record<MirBadgeState, MirBadgeSpec | null> = {
  reply_required: {
    label: 'Reply Required',
    icon: Reply,
    className:
      'bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/30 hover:bg-amber-500/20',
  },
  awaiting_reply: {
    label: 'Awaiting reply',
    icon: ArrowUpRight,
    className:
      'bg-blue-500/10 text-blue-700 dark:text-blue-400 border-blue-500/30 hover:bg-blue-500/20',
  },
  response_received: {
    label: 'Response Received',
    icon: MessageSquare,
    className:
      'bg-green-500/10 text-green-700 dark:text-green-400 border-green-500/30 hover:bg-green-500/20',
  },
  reply_sent: {
    label: 'Reply Sent',
    icon: CheckCheck,
    className:
      'bg-teal-500/10 text-teal-700 dark:text-teal-400 border-teal-500/30 hover:bg-teal-500/20',
  },
  none: null,
};

interface JobMirBadgeProps {
  mirBadgeState: MirBadgeState;
  /** Current work-order status; the MIR badge is hidden on terminal states. */
  workOrderStatus: string | null | undefined;
}

export function JobMirBadge({ mirBadgeState, workOrderStatus }: JobMirBadgeProps) {
  if (!clientConfig.features.requestMoreInfo) return null;
  // Terminal work orders (Completed/Rejected/Cancelled/Validated) have no live
  // MIR — mirrors MirStatusBadge and RequestMoreInfoButton (WRK-5571, WRK-5644).
  if (!canRequestMoreInfo(workOrderStatus ?? '')) return null;

  const spec = MIR_BADGE_SPECS[mirBadgeState];
  if (!spec) return null;

  const Icon = spec.icon;
  return (
    <Badge className={cn(spec.className, 'gap-1 text-nowrap')}>
      <Icon className="size-3 shrink-0" />
      {spec.label}
    </Badge>
  );
}
